import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

type QuickBooksServiceChoice = {
  value: string;
  label: string;
  description?: string | null;
};

type Props = {
  id: string;
  value: string;
  options: QuickBooksServiceChoice[];
  disabled?: boolean;
  onChange: (value: string) => void;
  /** Optional overrides for use by the Class / Location selectors. */
  ariaLabel?: string;
  emptyLabel?: string;
  searchPlaceholder?: string;
  noMatchesLabel?: string;
};

type MenuPosition = {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
};

/** A searchable replacement for the native QBO Service select; keeps QBO IDs intact. */
export function SearchableQuickBooksServiceSelect({
  id,
  value,
  options,
  disabled = false,
  onChange,
  ariaLabel = 'QuickBooks service',
  emptyLabel,
  searchPlaceholder = 'Search QuickBooks services…',
  noMatchesLabel = 'No matching QuickBooks services',
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [position, setPosition] = useState<MenuPosition | null>(null);

  const selected = options.find((option) => option.value === value);
  const selectedLabel = selected?.label ?? '';

  useEffect(() => {
    if (!open) setSearch(selectedLabel);
  }, [selectedLabel, open]);

  const matching = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return options.filter(
      (option) =>
        !term ||
        option.label.toLocaleLowerCase().includes(term) ||
        (option.description ?? '').toLocaleLowerCase().includes(term)
    );
  }, [options, search]);

  // Mount to document.body to avoid being clipped by the modal's scroll container.
  useEffect(() => {
    if (!open || disabled) return;

    const placeMenu = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const showAbove = below < 220 && above > below;
      const available = showAbove ? above : below;
      const maxHeight = Math.max(100, Math.min(300, available - 8));
      setPosition({
        left: Math.max(8, rect.left),
        width: Math.max(180, Math.min(rect.width, window.innerWidth - 16)),
        top: showAbove ? Math.max(8, rect.top - maxHeight - 6) : rect.bottom + 6,
        maxHeight,
      });
    };

    placeMenu();
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [open, disabled]);

  useEffect(() => {
    if (!open) return;
    const selectedRow = menuRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${activeIndex}"]`
    );
    selectedRow?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open, matching.length]);

  const close = () => {
    setOpen(false);
    setSearch(selectedLabel);
  };

  const choose = (newValue: string) => {
    onChange(newValue);
    setOpen(false);
    setSearch(options.find((option) => option.value === newValue)?.label ?? '');
    inputRef.current?.focus();
  };

  const startSearch = () => {
    if (disabled) return;
    setSearch('');
    setActiveIndex(0);
    setOpen(true);
  };

  return (
    <div
      className={`invoice-qbo-service-picker${disabled ? ' invoice-qbo-service-picker--disabled' : ''}`}
      ref={containerRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
    >
      <div
        className={`invoice-qbo-service-picker__control${open ? ' invoice-qbo-service-picker__control--open' : ''}`}
      >
        <Search size={15} aria-hidden="true" className="invoice-qbo-service-picker__search-icon" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-activedescendant={
            open && matching[activeIndex] ? `${id}-option-${activeIndex}` : undefined
          }
          autoComplete="off"
          disabled={disabled}
          value={search}
          placeholder={!open && !value && emptyLabel ? emptyLabel : searchPlaceholder}
          onFocus={startSearch}
          onChange={(event) => {
            setSearch(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              close();
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              if (!open) startSearch();
              else if (matching.length)
                setActiveIndex((index) => Math.min(index + 1, matching.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              if (!open) startSearch();
              else setActiveIndex((index) => Math.max(0, index - 1));
            } else if (event.key === 'Enter' && open) {
              event.preventDefault();
              if (matching[activeIndex]) choose(matching[activeIndex].value);
            }
          }}
        />
        {value && !disabled ? (
          <button
            type="button"
            className="invoice-qbo-service-picker__icon-button"
            aria-label={`Clear ${ariaLabel}`}
            title={`Clear ${ariaLabel}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange('');
              startSearch();
              inputRef.current?.focus();
            }}
          >
            <X size={15} />
          </button>
        ) : (
          <button
            type="button"
            className="invoice-qbo-service-picker__icon-button"
            aria-label={`Show ${ariaLabel} options`}
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (open) close();
              else {
                startSearch();
                inputRef.current?.focus();
              }
            }}
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>
      {open &&
        !disabled &&
        position &&
        createPortal(
          <div
            id={`${id}-listbox`}
            className="invoice-qbo-service-picker__menu"
            role="listbox"
            aria-label={ariaLabel}
            ref={menuRef}
            style={{
              position: 'fixed',
              left: position.left,
              top: position.top,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
            onMouseDown={(event) => event.preventDefault()}
          >
            {matching.length === 0 ? (
              <div className="invoice-qbo-service-picker__empty">{noMatchesLabel}</div>
            ) : (
              matching.map((option, index) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  id={`${id}-option-${index}`}
                  data-option-index={index}
                  key={option.value}
                  className={`invoice-qbo-service-picker__option${index === activeIndex ? ' invoice-qbo-service-picker__option--active' : ''}${option.value === value ? ' invoice-qbo-service-picker__option--selected' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(option.value)}
                >
                  <span className="invoice-qbo-service-picker__option-text">
                    <span className="invoice-qbo-service-picker__option-label">{option.label}</span>
                    {option.description && option.description !== option.label && (
                      <span className="invoice-qbo-service-picker__option-description">
                        {option.description}
                      </span>
                    )}
                  </span>
                  {option.value === value && (
                    <Check
                      size={15}
                      className="invoice-qbo-service-picker__check"
                      aria-hidden="true"
                    />
                  )}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
